import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { gerarIcsCompromisso } from "../utils/gerarIcs";
import "./EnviarAtaEmailModal.css";

// ✅ Logo hospedada no Cloudinary (URL pública) — mesma usada no e-mail de ata
const ONBOX_LOGO_URL = "https://res.cloudinary.com/dmecovtmm/image/upload/v1768829076/onbox/ob5.jpg";

function EnviarCompromissoEmailModal({ compromisso, participantes, usuarioId, containerId, onClose }) {
  const [destinatarios, setDestinatarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assunto, setAssunto] = useState(`Convite: ${compromisso.titulo}`);
  const [mensagemCustomizada, setMensagemCustomizada] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [nomeCriador, setNomeCriador] = useState("");
  const [emailExterno, setEmailExterno] = useState("");
  const [mostraConfirmacao, setMostraConfirmacao] = useState(false);

  // Formata data e hora
  const formatarDataHora = () => {
    const [ano, mes, dia] = compromisso.data_compromisso.split("-");
    const data = new Date(ano, mes - 1, dia);
    const diaSemana = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"][data.getDay()];
    const mesNome = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"][data.getMonth()];
    const hora = compromisso.hora_inicio ? compromisso.hora_inicio.slice(0, 5) : "horário não definido";
    return `${diaSemana}, ${dia} de ${mesNome} de ${ano} às ${hora}`;
  };

  // ✅ Gera a mensagem automática no mesmo padrão visual do e-mail de ata (tabela + logo + rodapé)
  const gerarMensagemAuto = () => {
    const dataHora = formatarDataHora();

    let detalhesExtra = "";
    if (compromisso.link_reuniao) {
      detalhesExtra += `
        Tipo: Online<br/>
        Link da reunião: <a href="${compromisso.link_reuniao}" style="color: #2563eb; text-decoration: none; word-break: break-all;">${compromisso.link_reuniao}</a><br/>`;
    } else if (compromisso.local) {
      detalhesExtra += `
        Tipo: Presencial<br/>
        Local: ${compromisso.local}<br/>`;
    }

    if (compromisso.descricao) {
      detalhesExtra += `
        Descrição: ${compromisso.descricao.replace(/\n/g, "<br/>")}<br/>`;
    }

    return `
<table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
  <tr>
    <td style="text-align: center; padding: 16px;">
      <img src="${ONBOX_LOGO_URL}" alt="ONBOX" style="height: 40px; margin-bottom: 12px;" />
    </td>
  </tr>
  <tr>
    <td style="padding: 16px; font-family: Arial, sans-serif; color: #333;">
      <p>Olá,</p>
      <p><strong>${nomeCriador}</strong> agendou uma reunião "<strong>${compromisso.titulo}</strong>" e você foi convidado(a) a participar.</p>
      <p style="font-size: 12px; color: #666; margin-top: 8px;">
        <strong>Detalhes do Compromisso:</strong><br/>
        Data: ${dataHora}<br/>
        ${detalhesExtra}
      </p>
      <p style="margin-top: 16px;">Segue em anexo o convite para adicionar ao seu calendário.</p>
      <p style="margin-top: 16px;">Atenciosamente,<br/><strong>ONBOX</strong></p>
      <hr style="border: none; border-top: 1px solid #ddd; margin: 2px 0;" />
      <p style="font-size: 11px; color: #999; margin: 0;">
        ONBOX | Plataforma de Gestão e Operações da Construção Civil <br/>
        <a href="https://onboxapp.com.br" style="color: #999; text-decoration: none;">https://onboxapp.com.br</a>
        fale conosco: <a href="mailto:contato@onboxapp.com.br" style="color: #999; text-decoration: none;">contato@onboxapp.com.br</a>
      </p>
    </td>
  </tr>
</table>
`;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);

      // Busca nome de quem criou o convite
      if (usuarioId) {
        const { data: userData } = await supabase
          .from("profiles")
          .select("nome, nickname")
          .eq("id", usuarioId)
          .single();
        setNomeCriador(userData?.nickname || userData?.nome || "Colega");
      }

      const internos = participantes.filter(p => p.usuario_id);
      const externos = participantes.filter(p => !p.usuario_id);

      let emailsInternos = {};
      if (internos.length) {
        const { data } = await supabase
          .from("profiles")
          .select("id, email")
          .in("id", internos.map(p => p.usuario_id));
        emailsInternos = (data || []).reduce((acc, p) => { acc[p.id] = p.email; return acc; }, {});
      }

      setDestinatarios([
        ...internos.map(p => ({
          id: p.id,
          nome: p.nome,
          email: emailsInternos[p.usuario_id] || "",
          externo: false,
          selecionado: !!emailsInternos[p.usuario_id],
        })),
        ...externos.map(p => ({
          id: p.id,
          nome: p.nome,
          email: p.email_externo || "",
          externo: true,
          selecionado: !!p.email_externo,
        })),
      ]);

      setLoading(false);
    })();
  }, [participantes, usuarioId]);

  // Gera mensagem quando componente monta
  useEffect(() => {
    if (nomeCriador) {
      setMensagemCustomizada(gerarMensagemAuto());
    }
  }, [nomeCriador, compromisso]);

  const validarEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");

  const toggleSelecionado = (id) =>
    setDestinatarios(prev => prev.map(d => d.id === id ? { ...d, selecionado: !d.selecionado } : d));

  const atualizarEmailExterno = (id, email) =>
    setDestinatarios(prev => prev.map(d => d.id === id ? { ...d, email } : d));

  const salvarEmailExterno = async (participanteId, email) => {
    if (!validarEmail(email)) return;
    await supabase.from("compromisso_participantes").update({ email_externo: email }).eq("id", participanteId);
  };

  const adicionarEmailExterno = () => {
    if (!emailExterno.trim() || !validarEmail(emailExterno)) {
      alert("Por favor, informe um e-mail válido.");
      return;
    }

    const novoDestinario = {
      id: `ext-${Date.now()}`,
      nome: emailExterno.split("@")[0],
      email: emailExterno,
      externo: true,
      selecionado: true,
    };

    setDestinatarios(prev => [...prev, novoDestinario]);
    setEmailExterno("");
  };

  const removerDestinario = (id) => {
    setDestinatarios(prev => prev.filter(d => d.id !== id));
  };

  const enviar = async () => {
    const selecionados = destinatarios.filter(d => d.selecionado);
    if (!selecionados.length) {
      alert("Selecione ao menos um destinatário.");
      return;
    }

    const semEmail = selecionados.filter(d => !validarEmail(d.email));
    if (semEmail.length) {
      alert(`Preencha um e-mail válido para: ${semEmail.map(d => d.nome).join(", ")}`);
      return;
    }

    // Mostra confirmação
    setMostraConfirmacao(true);
  };

  const confirmarEnvio = async () => {
    setMostraConfirmacao(false);
    setEnviando(true);
    setResultado(null);

    try {
      const ics = gerarIcsCompromisso(compromisso, destinatarios.filter(d => d.selecionado).map(d => d.email));
      const icsBase64 = btoa(unescape(encodeURIComponent(ics)));

      const ok = [];
      const falha = [];
      const selecionados = destinatarios.filter(d => d.selecionado);

      for (const d of selecionados) {
        try {
          console.log("🚀 Chamando função para:", d.email);

          const { data, error } = await supabase.functions.invoke("enviar-compromisso-email", {
            body: {
              to: [d.email],
              subject: assunto,
              html: mensagemCustomizada,
              icsBase64,
              icsFileName: "convite.ics",
            },
          });

          console.log("📬 Resposta da função:", { data, error });

          if (error || data?.error) throw new Error(data?.error || error.message);
          ok.push(d.nome);
        } catch (err) {
          console.error("💥 Erro ao enviar para", d.nome, err);
          falha.push({ nome: d.nome, motivo: err.message });
        }
      }

      setResultado({ ok, falha });
    } catch (err) {
      console.error("🔥 Erro geral:", err);
      alert(`Erro ao gerar convite ou enviar: ${err.message}`);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="enviar-ata-email-modal" style={{ maxWidth: 700 }}>
        <div className="enviar-ata-email-modal__header">
          <h3>📧 Enviar convite: {compromisso.titulo}</h3>
          <button className="enviar-ata-email-modal__close" onClick={onClose} style={{ cursor: "pointer" }}>✕</button>
        </div>

        {loading ? (
          <div className="enviar-ata-email-modal__loading">⏳ Carregando participantes...</div>
        ) : mostraConfirmacao ? (
          <div style={{ padding: "24px" }}>
            <div style={{ background: "#f0f7ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <h4 style={{ margin: "0 0 12px 0", color: "#1e40af" }}>📋 Confirmar envio</h4>
              <p style={{ margin: "8px 0", fontSize: 14 }}>
                <strong>Assunto:</strong> {assunto}
              </p>
              <p style={{ margin: "8px 0", fontSize: 14 }}>
                <strong>Destinatários:</strong> {destinatarios.filter(d => d.selecionado).map(d => d.nome).join(", ")}
              </p>
              <p style={{ margin: "8px 0", fontSize: 14 }}>
                <strong>Total:</strong> {destinatarios.filter(d => d.selecionado).length} email(s)
              </p>
            </div>

            <p style={{ margin: "16px 0", fontSize: 14, color: "#6b7280" }}>
              Tem certeza que deseja enviar o convite para {destinatarios.filter(d => d.selecionado).length} pessoa(s)?
            </p>

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                onClick={() => setMostraConfirmacao(false)}
                style={{
                  padding: "10px 16px",
                  border: "1px solid #d1d5db",
                  background: "white",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarEnvio}
                disabled={enviando}
                style={{
                  padding: "10px 16px",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                  opacity: enviando ? 0.7 : 1,
                }}
              >
                {enviando ? "Enviando..." : "Confirmar e enviar"}
              </button>
            </div>
          </div>
        ) : resultado ? (
          <div className="enviar-ata-email-modal__resultado" style={{ padding: "24px" }}>
            {resultado.ok.length > 0 && (
              <p className="enviar-ata-email-modal__ok" style={{ color: "#16a34a", marginBottom: 16 }}>
                ✅ Enviado com sucesso para: <strong>{resultado.ok.join(", ")}</strong>
              </p>
            )}
            {resultado.falha.length > 0 && (
              <div style={{ color: "#dc2626", marginBottom: 16 }}>
                <p style={{ marginBottom: 8 }}>❌ Falha ao enviar para:</p>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {resultado.falha.map((f, i) => (
                    <li key={i}>{f.nome} — {f.motivo}</li>
                  ))}
                </ul>
              </div>
            )}
            <button
              className="enviar-ata-email-modal__fechar-btn"
              onClick={onClose}
              style={{
                padding: "10px 16px",
                background: "#2563eb",
                color: "white",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 14,
                marginTop: 16,
              }}
            >
              Fechar
            </button>
          </div>
        ) : (
          <>
            <div className="enviar-ata-email-modal__body" style={{ maxHeight: "calc(80vh - 180px)", overflowY: "auto" }}>
              {/* Assunto */}
              <div className="enviar-ata-email-modal__field">
                <label style={{ fontWeight: 600, marginBottom: 8, display: "block" }}>Assunto *</label>
                <input
                  type="text"
                  value={assunto}
                  onChange={e => setAssunto(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Mensagem */}
              <div className="enviar-ata-email-modal__field">
                <label style={{ fontWeight: 600, marginBottom: 8, display: "block" }}>Mensagem *</label>
                <textarea
                  value={mensagemCustomizada}
                  onChange={e => setMensagemCustomizada(e.target.value)}
                  rows={10}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    fontSize: 12,
                    fontFamily: "monospace",
                    boxSizing: "border-box",
                    resize: "vertical",
                  }}
                />
                <p style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  💡 Mensagem automática gerada no padrão visual da ata (logo ONBOX + rodapé). Você pode personalizar se desejar.
                </p>
              </div>

              {/* Destinatários internos */}
              <div className="enviar-ata-email-modal__field">
                <label style={{ fontWeight: 600, marginBottom: 8, display: "block" }}>👥 Participantes registrados</label>
                {destinatarios.filter(d => !d.externo).length === 0 ? (
                  <p style={{ color: "#6b7280", fontSize: 14 }}>Nenhum participante interno adicionado.</p>
                ) : (
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8, maxHeight: 200, overflowY: "auto" }}>
                    {destinatarios
                      .filter(d => !d.externo)
                      .map(d => (
                        <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                          <input
                            type="checkbox"
                            checked={d.selecionado}
                            onChange={() => toggleSelecionado(d.id)}
                            style={{ cursor: "pointer", width: "16px" }}
                          />
                          <span style={{ flex: 1, fontSize: 14 }}>{d.nome}</span>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>{d.email}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Destinatários externos */}
              <div className="enviar-ata-email-modal__field">
                <label style={{ fontWeight: 600, marginBottom: 8, display: "block" }}>🔗 Participantes externos</label>
                {destinatarios.filter(d => d.externo).length > 0 ? (
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8, maxHeight: 200, overflowY: "auto", marginBottom: 12 }}>
                    {destinatarios
                      .filter(d => d.externo)
                      .map(d => (
                        <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, borderBottom: "1px solid #f3f4f6" }}>
                          <input
                            type="checkbox"
                            checked={d.selecionado}
                            onChange={() => toggleSelecionado(d.id)}
                            style={{ cursor: "pointer", width: "16px" }}
                          />
                          <span style={{ flex: 1, fontSize: 14 }}>{d.email}</span>
                          <button
                            onClick={() => removerDestinario(d.id)}
                            style={{
                              padding: "4px 8px",
                              background: "#fee2e2",
                              color: "#dc2626",
                              border: "none",
                              borderRadius: 4,
                              cursor: "pointer",
                              fontSize: 12,
                            }}
                          >
                            Remover
                          </button>
                        </div>
                      ))}
                  </div>
                ) : null}

                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="email"
                    value={emailExterno}
                    onChange={e => setEmailExterno(e.target.value)}
                    placeholder="exemplo@email.com"
                    onKeyPress={e => e.key === "Enter" && adicionarEmailExterno()}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      fontSize: 14,
                      boxSizing: "border-box",
                    }}
                  />
                  <button
                    onClick={adicionarEmailExterno}
                    style={{
                      padding: "8px 16px",
                      background: "#2563eb",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontSize: 14,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Adicionar
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="enviar-ata-email-modal__footer">
              <button
                onClick={onClose}
                disabled={enviando}
                style={{
                  padding: "10px 16px",
                  border: "1px solid #d1d5db",
                  background: "white",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Cancelar
              </button>
              <button
                onClick={enviar}
                disabled={enviando || destinatarios.filter(d => d.selecionado).length === 0}
                style={{
                  padding: "10px 16px",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                  opacity: enviando || destinatarios.filter(d => d.selecionado).length === 0 ? 0.7 : 1,
                }}
              >
                {enviando ? "Enviando..." : `Enviar convite (${destinatarios.filter(d => d.selecionado).length})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EnviarCompromissoEmailModal;